import { BRAND_X_SUBTYPE_FILTER_ENABLED } from 'src/constants/moment-feature-flags';

/** ES bool 절 한 덩이. 모양의 주인은 ES 조립 쪽이라 여기서는 통과만 시킨다 */
type EsBoolClause = Record<string, any>;

/** 화면 필터가 ES 조립기에 넘기는 filter 디스크립터 */
type EsFilterDescriptor = Record<string, any>;

const BRAND_X_KIND_CHIP_IDS = ['x', 'x-quote', 'x-repost'];
const BRAND_X_KIND_CHIP_SET = new Set(BRAND_X_KIND_CHIP_IDS);

const BRAND_INSTAGRAM_KIND_CHIP_IDS = ['instagram-post', 'instagram-story'];
const BRAND_INSTAGRAM_KIND_CHIP_SET = new Set(BRAND_INSTAGRAM_KIND_CHIP_IDS);

/** 메인 채널 칩에서 제외 — 서브타입 전용 ID */
const BRAND_CHANNEL_SUBTYPE_ONLY_CHIP_IDS = [
  'x-quote',
  'x-repost',
  'instagram-post',
  'instagram-story',
];
const BRAND_CHANNEL_SUBTYPE_ONLY_CHIP_SET = new Set(
  BRAND_CHANNEL_SUBTYPE_ONLY_CHIP_IDS,
);

/** @deprecated BRAND_CHANNEL_SUBTYPE_ONLY_CHIP_SET */
const BRAND_X_SUBTYPE_ONLY_CHIP_SET = BRAND_CHANNEL_SUBTYPE_ONLY_CHIP_SET;

/** 브랜드뷰 X 칩 기본값 — 본문·인용 ON, 리포스트 OFF */
export const DEFAULT_X_SUBTYPE_PLATFORMS = ['x', 'x-quote'];

/** 브랜드뷰 Instagram 칩 기본값 — 포스트·스토리 모두 ON */
export const DEFAULT_INSTAGRAM_SUBTYPE_PLATFORMS = [
  'instagram-post',
  'instagram-story',
];

/** 사이드바 칩 카운트 집계용 — X 서브타입 선택과 무관하게 3종 모두 포함 */
export const ALL_BRAND_X_SUBTYPE_PLATFORMS = [...BRAND_X_KIND_CHIP_IDS];

/** 사이드바 칩 카운트 집계용 — Instagram 서브타입 2종 */
export const ALL_BRAND_INSTAGRAM_SUBTYPE_PLATFORMS = [
  ...BRAND_INSTAGRAM_KIND_CHIP_IDS,
];


function toNormalizedArray(values: unknown): string[] {
  if (!Array.isArray(values)) return [];
  return Array.from(
    new Set(
      values
        .map((value: unknown) => String(value || '').trim().toLowerCase())
        .filter(Boolean)
    )
  );
}

export function getMainPlatforms(platforms: unknown): string[] {
  return toNormalizedArray(platforms).filter(
    (platformId) => !BRAND_CHANNEL_SUBTYPE_ONLY_CHIP_SET.has(platformId),
  );
}

/**
 * Instagram 포스트(instagram-post)·스토리(instagram-story) 칩 선택 목록
 */
export function getInstagramKindPlatforms({
  platforms,
  instagramPlatforms,
}: { platforms?: unknown; instagramPlatforms?: unknown } = {}): string[] {
  const hasExplicitIgPlatforms = Array.isArray(instagramPlatforms);
  const fromIg = toNormalizedArray(instagramPlatforms).filter((platformId) =>
    BRAND_INSTAGRAM_KIND_CHIP_SET.has(platformId),
  );
  if (hasExplicitIgPlatforms) return fromIg;
  return toNormalizedArray(platforms).filter((platformId) =>
    BRAND_INSTAGRAM_KIND_CHIP_SET.has(platformId),
  );
}

/** @deprecated getInstagramKindPlatforms */
export function getInstagramSubtypes(args: { platforms?: unknown; instagramPlatforms?: unknown }): string[] {
  return getInstagramKindPlatforms(args);
}

function isAllBrandInstagramKindsSelected(instagramPlatforms: unknown): boolean {
  const kinds = getInstagramKindPlatforms({ instagramPlatforms });
  if (kinds.length === 0) return false;
  return BRAND_INSTAGRAM_KIND_CHIP_IDS.every((id) => kinds.includes(id));
}

export function isPartialBrandInstagramKindSelection(instagramPlatforms: unknown): boolean {
  const kinds = getInstagramKindPlatforms({ instagramPlatforms });
  if (kinds.length === 0) return false;
  return !isAllBrandInstagramKindsSelected(instagramPlatforms);
}

/**
 * Instagram 포스트/스토리 토글 후 instagramPlatforms 정규화
 */
export function normalizeBrandInstagramKindSelectionAfterToggle(
  prevKinds: unknown,
  toggledId: unknown,
): string[] {
  const id = String(toggledId || '').trim().toLowerCase();
  if (!BRAND_INSTAGRAM_KIND_CHIP_SET.has(id)) {
    return Array.from(new Set(Array.isArray(prevKinds) ? prevKinds : []));
  }

  const kinds = Array.isArray(prevKinds)
    ? [...prevKinds]
    : [...DEFAULT_INSTAGRAM_SUBTYPE_PLATFORMS];

  if (kinds.includes(id)) {
    return kinds.filter((platformId) => platformId !== id);
  }

  return Array.from(new Set([...kinds, id]));
}

/**
 * Instagram 포스트를 포스트·스토리 칩 ID로 분류
 * @param {Record<string, unknown>} post
 * @returns {'instagram-post'|'instagram-story'|null}
 */
export function classifyInstagramPostKindChipId(post: any): 'instagram-post' | 'instagram-story' | null {
  const src = String(post?.source || post?.platform || '').trim().toLowerCase();
  if (src !== 'instagram') return null;
  return post?.isStory === true ? 'instagram-story' : 'instagram-post';
}

/** Instagram 채널 문서 한정 절 */
const IG_SOURCE_TERM = { term: { source: 'instagram' } };
/** 스토리 판정 — 색인 루트 플래그. true일 때만 필드가 존재한다 */
const IG_STORY_TERM = { term: { isStory: true } };

/**
 * Instagram 서브타입 1종의 ES bool — 필터·집계 공용 단일 소스.
 *
 * X의 `buildBrandXKindEsBool`과 같은 규약이다. 사이드바 필터와 대시보드 칩 집계가
 * 같은 절을 쓰므로 칩 숫자와 필터 결과가 어긋나지 않는다.
 *
 * @param {'instagram-post'|'instagram-story'} chipId
 */
export function buildBrandInstagramKindEsBool(chipId: unknown): EsBoolClause | null {
  if (chipId === 'instagram-story') {
    return { bool: { filter: [IG_SOURCE_TERM, IG_STORY_TERM] } };
  }
  return {
    bool: {
      filter: [IG_SOURCE_TERM],
      must_not: [IG_STORY_TERM],
    },
  };
}

/**
 * Instagram 서브타입만 부분 선택 시 ES bool
 * @param {string[]} instagramPlatforms
 * @returns {Record<string, unknown>|null} ES bool 절 (TS 호출부가 절로 쓸 수 있어야 한다)
 */
export function buildBrandInstagramKindEsBoolShould(instagramPlatforms: unknown): EsBoolClause | null {
  if (!isPartialBrandInstagramKindSelection(instagramPlatforms)) return null;

  const kinds = getInstagramKindPlatforms({ instagramPlatforms });
  const clauses = ['instagram-story', 'instagram-post']
    .filter((chipId) => kinds.includes(chipId))
    .map(buildBrandInstagramKindEsBool);

  if (clauses.length === 0) return null;
  if (clauses.length === 1) return clauses[0];
  return { bool: { should: clauses, minimum_should_match: 1 } };
}

/**
 * 전체 채널 + Instagram 서브타입 일부 OFF — ES must_not
 * @param {string[]} instagramPlatforms
 * @returns {Object[]}
 */
export function buildBrandInstagramKindEsMustNotFilters(instagramPlatforms: unknown): EsFilterDescriptor[] {
  if (!isPartialBrandInstagramKindSelection(instagramPlatforms)) return [];

  const kinds = getInstagramKindPlatforms({ instagramPlatforms });
  const filters: EsFilterDescriptor[] = [];

  const pushMustNot = (clause: EsBoolClause | null) => {
    filters.push({
      type: 'bool_should',
      query: { bool: { must_not: [clause] } },
    });
  };

  if (!kinds.includes('instagram-story')) {
    pushMustNot({
      bool: {
        filter: [
          { term: { source: 'instagram' } },
          { term: { isStory: true } },
        ],
      },
    });
  }

  if (!kinds.includes('instagram-post')) {
    pushMustNot({
      bool: {
        filter: [{ term: { source: 'instagram' } }],
        must_not: [{ term: { isStory: true } }],
      },
    });
  }

  return filters;
}

/**
 * X 본문(x)·인용(x-quote)·리포스트(x-repost) 칩 선택 목록
 */
export function getXKindPlatforms({
  platforms,
  xPlatforms,
}: { platforms?: unknown; xPlatforms?: unknown } = {}): string[] {
  const hasExplicitXPlatforms = Array.isArray(xPlatforms);
  const fromX = toNormalizedArray(xPlatforms).filter((platformId) =>
    BRAND_X_KIND_CHIP_SET.has(platformId),
  );
  if (hasExplicitXPlatforms) return fromX;
  return toNormalizedArray(platforms).filter((platformId) =>
    BRAND_X_KIND_CHIP_SET.has(platformId),
  );
}

/** @deprecated getXKindPlatforms 사용 */
export function getXSubtypes(args: { platforms?: unknown; xPlatforms?: unknown }): string[] {
  return getXKindPlatforms(args);
}

function isAllBrandXKindsSelected(xPlatforms: unknown): boolean {
  const kinds = getXKindPlatforms({ xPlatforms });
  if (kinds.length === 0) return false;
  return BRAND_X_KIND_CHIP_IDS.every((id) => kinds.includes(id));
}

/**
 * X 채널이 현재 필터에 포함되는지 (세부 1개 이상 ON)
 * @param {Object} [filter]
 * @returns {boolean}
 */
function isBrandXChannelFilterActive(filter: any): boolean {
  const mainPlatforms = getMainPlatforms(filter?.platforms);
  const kinds = getXKindPlatforms({
    platforms: filter?.platforms,
    xPlatforms: filter?.xPlatforms,
  });
  if (mainPlatforms.includes('x')) return kinds.length > 0;
  if (mainPlatforms.length === 0) return kinds.length > 0;
  return false;
}

/**
 * Instagram 채널이 현재 필터에 포함되는지 (세부 1개 이상 ON)
 * @param {Object} [filter]
 * @returns {boolean}
 */
export function isBrandInstagramChannelFilterActive(filter: any): boolean {
  const mainPlatforms = getMainPlatforms(filter?.platforms);
  const kinds = getInstagramKindPlatforms({
    platforms: filter?.platforms,
    instagramPlatforms: filter?.instagramPlatforms,
  });
  if (mainPlatforms.includes('instagram')) return kinds.length > 0;
  if (mainPlatforms.length === 0) return kinds.length > 0;
  return false;
}

/**
 * SearchClientFilter용 브랜드 플랫폼 필터 옵션
 * @param {Object} [sidebarFilter]
 */
export function buildBrandMentionPlatformFilterOptions(sidebarFilter: any) {
  const effective = sidebarFilter || {};
  return {
    selectedPlatformIds: getSelectedPlatformIds(effective),
    selectedMainPlatformIds: getMainPlatforms(effective?.platforms),
    isAllBrandChannels: isBrandChannelAllSelected(effective),
    brandXChannelActive: isBrandXChannelFilterActive(effective),
    brandInstagramChannelActive: isBrandInstagramChannelFilterActive(effective),
  };
}

/** 리포스트 토글 ON — ES + reposts[] supplement 포함 */
export function isBrandMentionRepostSupplementEnabled(sidebarFilter: any): boolean {
  return getXKindPlatforms({
    platforms: sidebarFilter?.platforms,
    xPlatforms: sidebarFilter?.xPlatforms,
  }).includes('x-repost');
}

/** 브랜드 채널 필터 「전체」 칩 — 메인 채널 미선택 (X 인용·리포스트와 별개) */
export function isBrandChannelAllSelected(filter: any): boolean {
  const mainPlatforms = getMainPlatforms(filter?.platforms);
  return mainPlatforms.length === 0;
}

/**
 * X·인용·리포스트 토글 후 xPlatforms 정규화
 * X 본문 ON 시 인용(x-quote) 기본 포함
 */
export function normalizeBrandXKindSelectionAfterToggle(prevKinds: unknown, toggledId: unknown): string[] {
  const id = String(toggledId || '').trim().toLowerCase();
  if (!BRAND_X_KIND_CHIP_SET.has(id)) {
    return Array.from(new Set(Array.isArray(prevKinds) ? prevKinds : []));
  }

  const kinds = Array.isArray(prevKinds)
    ? [...prevKinds]
    : [...DEFAULT_X_SUBTYPE_PLATFORMS];

  if (kinds.includes(id)) {
    return kinds.filter((platformId) => platformId !== id);
  }

  const next = [...kinds, id];
  if (id === 'x' && !next.includes('x-quote')) {
    next.push('x-quote');
  }
  return Array.from(new Set(next));
}

export function isPartialBrandXKindSelection(xPlatforms: unknown): boolean {
  if (!BRAND_X_SUBTYPE_FILTER_ENABLED) return false;
  const kinds = getXKindPlatforms({ xPlatforms });
  if (kinds.length === 0) return false;
  return !isAllBrandXKindsSelected(xPlatforms);
}

/**
 * X 포스트를 본문·인용·리포스트 칩 ID로 분류 (코퍼스 모드 클라 판정).
 *
 * 인용 플래그가 이름 두 개로 존재한다 — **ES 색인 필드는 `isQuoted`**,
 * **클라 관례명은 `isQuote`**(`src/utils/moments/domain/moment-ingest.ts`의
 * `applyQuoteRepostTypeFlags`가 `originPost.type`에서 파생시키고 카드·표·칩이 전부 이
 * 이름을 쓴다). 둘 다 본다.
 * 리포스트는 양쪽이 `isRepost`로 같다.
 *
 * 플래그를 1순위로 두어야 ES 필터·집계(`buildBrandXKindEsBool`)와 기준이 일치한다.
 * `originPost.type`은 플래그가 없는 과거 문서용 폴백.
 *
 * @param {Record<string, unknown>} post
 * @returns {'x'|'x-quote'|'x-repost'|null}
 */
export function classifyXPostKindChipId(post: any): string | null {
  const src = String(post?.source || post?.platform || '').trim().toLowerCase();
  if (src !== 'x' && src !== 'twitter') return null;

  if (post?.isQuoted === true || post?.isQuote === true) return 'x-quote';
  if (post?.isRepost === true) return 'x-repost';

  const originType = String(
    post?.originPost?.type || post?._source?.originPost?.type || '',
  ).trim().toLowerCase();
  if (originType === 'quote') return 'x-quote';
  if (originType === 'repost' || originType === 'retweet') return 'x-repost';
  return 'x';
}

function getSelectedPlatformIds(filter: any): string[] {
  const mainPlatforms = getMainPlatforms(filter?.platforms);
  const xKinds = getXKindPlatforms({
    platforms: filter?.platforms,
    xPlatforms: filter?.xPlatforms,
  });
  const igKinds = getInstagramKindPlatforms({
    platforms: filter?.platforms,
    instagramPlatforms: filter?.instagramPlatforms,
  });

  const allX = isAllBrandXKindsSelected(filter?.xPlatforms);
  const allIg = isAllBrandInstagramKindsSelected(filter?.instagramPlatforms);

  // 전체 + X·Instagram 세부 모두 ON → 플랫폼 제한 없음
  if (mainPlatforms.length === 0 && allX && allIg) {
    return [];
  }

  // 전체 선택: 비-X·비-IG 채널 제한 없음, 세부 칩만 적용
  if (mainPlatforms.length === 0) {
    return Array.from(new Set([...xKinds, ...igKinds]));
  }

  const mainHasX = mainPlatforms.includes('x');
  const mainHasIg = mainPlatforms.includes('instagram');
  const partialX = isPartialBrandXKindSelection(filter?.xPlatforms);
  const partialIg = isPartialBrandInstagramKindSelection(filter?.instagramPlatforms);

  let result = [...mainPlatforms];

  if (mainHasX) {
    if (partialX) {
      result = result.filter((id) => id !== 'x' && id !== 'twitter');
      result = [...result, ...xKinds];
    } else {
      result = [...result, ...xKinds];
    }
  }

  if (mainHasIg) {
    if (partialIg) {
      result = result.filter((id) => id !== 'instagram');
      result = [...result, ...igKinds];
    } else {
      result = [...result, ...igKinds];
    }
  }

  return Array.from(new Set(result));
}

/** X 채널 문서 한정 절 (source 별칭 포함) */
const X_SOURCE_TERMS = { terms: { source: ['x', 'twitter'] } };
/** 인용·리포스트 판정 — 색인 루트 플래그. true일 때만 필드가 존재한다 */
const X_QUOTE_TERM = { term: { isQuoted: true } };
const X_REPOST_TERM = { term: { isRepost: true } };

/**
 * X 서브타입 1종의 ES bool — 필터·집계 공용 단일 소스.
 *
 * 판정은 색인 루트 플래그(`isQuoted`·`isRepost`)만 쓴다. 둘은 상호 배타이고
 * 원본은 "둘 다 아님"으로 정의한다.
 * `originPost.type`은 매핑에 없어 ES에서 쓸 수 없다(`_source`에만 존재).
 *
 * @param {'x'|'x-quote'|'x-repost'} chipId
 */
export function buildBrandXKindEsBool(chipId: unknown): EsBoolClause | null {
  if (chipId === 'x-quote') {
    return { bool: { filter: [X_SOURCE_TERMS, X_QUOTE_TERM] } };
  }
  if (chipId === 'x-repost') {
    return { bool: { filter: [X_SOURCE_TERMS, X_REPOST_TERM] } };
  }
  return {
    bool: {
      filter: [X_SOURCE_TERMS],
      must_not: [X_QUOTE_TERM, X_REPOST_TERM],
    },
  };
}

/**
 * X 서브타입만 부분 선택 시 ES bool
 * @param {string[]} xPlatforms
 * @returns {Record<string, unknown>|null} ES bool 절 (TS 호출부가 절로 쓸 수 있어야 한다)
 */
export function buildBrandXOriginKindEsBoolShould(xPlatforms: unknown): EsBoolClause | null {
  if (!isPartialBrandXKindSelection(xPlatforms)) return null;

  const kinds = getXKindPlatforms({ xPlatforms });
  const clauses = ['x-quote', 'x-repost', 'x']
    .filter((chipId) => kinds.includes(chipId))
    .map(buildBrandXKindEsBool);

  if (clauses.length === 0) return null;
  if (clauses.length === 1) return clauses[0];
  return { bool: { should: clauses, minimum_should_match: 1 } };
}

/**
 * 전체 채널 + X 서브타입 일부 OFF — 해당 X 유형만 ES에서 제외
 * @param {string[]} xPlatforms
 * @returns {Object[]}
 */
export function buildBrandXOriginKindEsMustNotFilters(xPlatforms: unknown): EsFilterDescriptor[] {
  if (!isPartialBrandXKindSelection(xPlatforms)) return [];

  const kinds = getXKindPlatforms({ xPlatforms });
  const filters: EsFilterDescriptor[] = [];

  const pushMustNot = (clause: EsBoolClause | null) => {
    filters.push({
      type: 'bool_should',
      query: { bool: { must_not: [clause] } },
    });
  };

  for (const chipId of ['x-quote', 'x-repost', 'x']) {
    if (!kinds.includes(chipId)) pushMustNot(buildBrandXKindEsBool(chipId));
  }

  return filters;
}

// ─────────────────────────────────────────────────────────────────────────────
// TikTok 광고 세부 필터 — 「세부 채널 필터」의 세 번째 갈래
//
// ⚠️ **X·Instagram 과 모양이 다르다.** 저쪽은 한 글이 세부 칩 «하나»에 속하지만
//    (포스트냐 스토리냐), TikTok 은 **축이 둘이고 서로 독립**이다:
//      ① 광고 유형  일반 | 틱톡샵 | 협찬     (`adMeta.adType`)
//      ② 광고 집행  일반 | 광고집행          (`adMeta.adBoost`)
//    한 글이 「협찬」이면서 동시에 「광고집행」일 수 있다. 그래서 `xPlatforms` 처럼
//    `platforms` 배열에 섞지 않고 **자기 칸 둘**(`tiktokAdTypes`·`tiktokAdBoosts`)을 쓴다 —
//    섞으면 `getMainPlatforms`·`getSelectedPlatformIds` 가 서 있는 「한 글 = 칩 하나」
//    전제가 깨진다.
//
// ⚠️ **「일반」은 「값이 organic」이 아니라 「틱톡샵도 협찬도 아님」이다.** 운영 실측에서
//    `adMeta` 가 실린 틱톡 문서는 일부뿐이라, organic 만 고르면
//    필드가 없는 78%가 통째로 사라진다. Instagram 포스트를 `must_not(isStory)` 로
//    정의하는 것과 같은 규약이다.
// ─────────────────────────────────────────────────────────────────────────────

/** 광고 유형 축 — 칩 세 개 */
export const BRAND_TIKTOK_AD_TYPE_CHIP_IDS = ['tiktok-organic', 'tiktok-shop', 'tiktok-paid'];
const BRAND_TIKTOK_AD_TYPE_CHIP_SET = new Set(BRAND_TIKTOK_AD_TYPE_CHIP_IDS);

/** 광고 집행 축 — 칩 두 개 */
export const BRAND_TIKTOK_AD_BOOST_CHIP_IDS = ['tiktok-noboost', 'tiktok-boost'];
const BRAND_TIKTOK_AD_BOOST_CHIP_SET = new Set(BRAND_TIKTOK_AD_BOOST_CHIP_IDS);

/** 기본값 — 두 축 다 전부 켜짐(= 필터 없음) */
export const DEFAULT_TIKTOK_AD_TYPE_CHIPS = [...BRAND_TIKTOK_AD_TYPE_CHIP_IDS];
export const DEFAULT_TIKTOK_AD_BOOST_CHIPS = [...BRAND_TIKTOK_AD_BOOST_CHIP_IDS];

const TIKTOK_SOURCE_TERM = { term: { source: 'tiktok' } };
/** 「일반」이 아닌 광고 유형 — 이 둘만 색인이 뜻을 갖는다(organic·unknown 은 일반으로 본다) */
const TIKTOK_TAGGED_AD_TYPES = ['commission', 'paid'];
const TIKTOK_TAGGED_AD_TYPE_TERMS = { terms: { 'adMeta.adType': TIKTOK_TAGGED_AD_TYPES } };
const TIKTOK_AD_BOOST_TERM = { term: { 'adMeta.adBoost': true } };

const pickChips = (values: unknown, chipSet: Set<string>): string[] =>
  toNormalizedArray(values).filter((id) => chipSet.has(id));

/** 고른 광고 유형 칩 */
export function getTiktokAdTypeChips(tiktokAdTypes: unknown): string[] {
  return pickChips(tiktokAdTypes, BRAND_TIKTOK_AD_TYPE_CHIP_SET);
}

/** 고른 광고 집행 칩 */
export function getTiktokAdBoostChips(tiktokAdBoosts: unknown): string[] {
  return pickChips(tiktokAdBoosts, BRAND_TIKTOK_AD_BOOST_CHIP_SET);
}

/**
 * **화면에 켜 보일 칩** — 값이 없으면 「전부 켜짐」이다.
 *
 * ⚠️ 고르기(`getTiktokAdTypeChips`)와 **일부러 다르다.** 저쪽은 저장된 값을 그대로 읽어
 * 빈 배열을 돌려주는데, 화면이 그걸 그대로 그리면 **칩은 전부 꺼져 보이는데 조회는 전체**인
 * 어긋난 상태가 된다(빈 값 = 「전체」가 이 축의 규약이므로). 값이 없는 경우는 셋이나 된다 —
 * 이 칸이 생기기 전의 저장 필터 · 다른 화면이 만든 필터 객체 · 개발 중 핫리로드로 남은 상태.
 * 그래서 **그리는 쪽은 늘 이 함수를 쓴다**(Instagram 포스트·스토리가 둘 다 켜져 보이는 것과 같은 첫 모습).
 */
export function resolveTiktokAdTypeChipSelection(tiktokAdTypes: unknown): string[] {
  const chips = getTiktokAdTypeChips(tiktokAdTypes);
  return chips.length > 0 ? chips : [...BRAND_TIKTOK_AD_TYPE_CHIP_IDS];
}

export function resolveTiktokAdBoostChipSelection(tiktokAdBoosts: unknown): string[] {
  const chips = getTiktokAdBoostChips(tiktokAdBoosts);
  return chips.length > 0 ? chips : [...BRAND_TIKTOK_AD_BOOST_CHIP_IDS];
}

/**
 * 한 축이 **일부만** 골라졌는가.
 *
 * 하나도 안 골랐으면 `false` 다 — X·Instagram 과 같은 규약으로 「전체」로 본다
 * (칩을 다 끄면 조회가 0건이 되는 것이 아니라 필터가 없는 것이다).
 */
const isPartialChipSelection = (chips: string[], allIds: string[]): boolean =>
  chips.length > 0 && chips.length < allIds.length;

export function isPartialTiktokAdTypeSelection(tiktokAdTypes: unknown): boolean {
  return isPartialChipSelection(getTiktokAdTypeChips(tiktokAdTypes), BRAND_TIKTOK_AD_TYPE_CHIP_IDS);
}

export function isPartialTiktokAdBoostSelection(tiktokAdBoosts: unknown): boolean {
  return isPartialChipSelection(
    getTiktokAdBoostChips(tiktokAdBoosts),
    BRAND_TIKTOK_AD_BOOST_CHIP_IDS,
  );
}

/**
 * TikTok 광고 칩 1종의 ES bool — 필터·집계 공용 단일 소스.
 *
 * `buildBrandInstagramKindEsBool` 과 같은 규약이다. 사이드바 필터와 칩 카운트가
 * 같은 절을 쓰므로 숫자와 결과가 어긋나지 않는다.
 */
export function buildTiktokAdChipEsBool(chipId: unknown): EsBoolClause | null {
  switch (String(chipId || '').trim().toLowerCase()) {
    case 'tiktok-shop':
      return {
        bool: { filter: [TIKTOK_SOURCE_TERM, { term: { 'adMeta.adType': 'commission' } }] },
      };
    case 'tiktok-paid':
      return { bool: { filter: [TIKTOK_SOURCE_TERM, { term: { 'adMeta.adType': 'paid' } }] } };
    case 'tiktok-organic':
      return {
        bool: { filter: [TIKTOK_SOURCE_TERM], must_not: [TIKTOK_TAGGED_AD_TYPE_TERMS] },
      };
    case 'tiktok-boost':
      return { bool: { filter: [TIKTOK_SOURCE_TERM, TIKTOK_AD_BOOST_TERM] } };
    case 'tiktok-noboost':
      return { bool: { filter: [TIKTOK_SOURCE_TERM], must_not: [TIKTOK_AD_BOOST_TERM] } };
    default:
      return null;
  }
}

/**
 * 안 고른 칩을 `must_not` 으로 뺀다 — **다른 채널은 건드리지 않는다.**
 *
 * Instagram 의 `buildBrandInstagramKindEsMustNotFilters` 와 같은 모양이다.
 * 두 축은 각자 판정해 각자 절을 내므로 AND 로 겹친다(「협찬만」 + 「광고집행 뺀 것」이 된다).
 */
export function buildTiktokAdEsMustNotFilters({
  tiktokAdTypes,
  tiktokAdBoosts,
}: { tiktokAdTypes?: unknown; tiktokAdBoosts?: unknown } = {}): EsFilterDescriptor[] {
  const filters: EsFilterDescriptor[] = [];

  const pushMustNotForUnselected = (chips: string[], allIds: string[], isPartial: boolean) => {
    if (!isPartial) return;
    for (const chipId of allIds) {
      if (chips.includes(chipId)) continue;
      filters.push({
        type: 'bool_should',
        query: { bool: { must_not: [buildTiktokAdChipEsBool(chipId)] } },
      });
    }
  };

  pushMustNotForUnselected(
    getTiktokAdTypeChips(tiktokAdTypes),
    BRAND_TIKTOK_AD_TYPE_CHIP_IDS,
    isPartialTiktokAdTypeSelection(tiktokAdTypes),
  );
  pushMustNotForUnselected(
    getTiktokAdBoostChips(tiktokAdBoosts),
    BRAND_TIKTOK_AD_BOOST_CHIP_IDS,
    isPartialTiktokAdBoostSelection(tiktokAdBoosts),
  );

  return filters;
}

/**
 * 칩 하나를 눌렀을 때의 다음 선택 — 축 안에서만 토글한다.
 *
 * ⚠️ **마지막 하나는 못 끈다.** 축을 통째로 비우면 「전체」와 구별이 안 되어
 * 화면에는 전부 켜진 것처럼 보이는데 필터는 없는 상태가 된다(사용자가 못 읽는다).
 */
export function normalizeTiktokAdChipSelectionAfterToggle(
  prevChips: unknown,
  toggledId: unknown,
  allIds: string[],
): string[] {
  const id = String(toggledId || '').trim().toLowerCase();
  if (!allIds.includes(id)) return pickChips(prevChips, new Set(allIds));

  const chips = pickChips(prevChips, new Set(allIds));
  const current = chips.length > 0 ? chips : [...allIds];
  if (!current.includes(id)) return allIds.filter((chipId) => current.includes(chipId) || chipId === id);
  if (current.length === 1) return current;
  return current.filter((chipId) => chipId !== id);
}

/**
 * 틱톡 글 한 건이 속하는 칩 — **축마다 하나씩, 늘 두 개**(코퍼스 모드 클라 판정·칩 카운트).
 *
 * ES 절(`buildTiktokAdChipEsBool`)과 판정 기준이 같아야 해서 「일반」을 `commission`·`paid`
 * 가 아닌 것으로 본다 — `adMeta` 가 통째로 없는 글도 「일반」이다.
 */
export function classifyTiktokAdChipIds(post: any): string[] {
  const src = String(post?.source || post?.platform || '').trim().toLowerCase();
  if (src !== 'tiktok') return [];

  const adType = String(post?.adMeta?.adType ?? '').trim().toLowerCase();
  const typeChip = adType === 'commission'
    ? 'tiktok-shop'
    : (adType === 'paid' ? 'tiktok-paid' : 'tiktok-organic');
  const boostChip = post?.adMeta?.adBoost === true ? 'tiktok-boost' : 'tiktok-noboost';
  return [typeChip, boostChip];
}

/**
 * 틱톡 글이 고른 칩에 걸리는가 — 두 축을 **AND** 로 본다.
 * 틱톡이 아닌 글은 이 필터와 무관하므로 `true`.
 */
export function matchesTiktokAdChips(
  post: any,
  { tiktokAdTypes, tiktokAdBoosts }: { tiktokAdTypes?: unknown; tiktokAdBoosts?: unknown } = {},
): boolean {
  const chips = classifyTiktokAdChipIds(post);
  if (chips.length === 0) return true;

  if (isPartialTiktokAdTypeSelection(tiktokAdTypes)
    && !getTiktokAdTypeChips(tiktokAdTypes).includes(chips[0])) {
    return false;
  }
  if (isPartialTiktokAdBoostSelection(tiktokAdBoosts)
    && !getTiktokAdBoostChips(tiktokAdBoosts).includes(chips[1])) {
    return false;
  }
  return true;
}

const X_SOURCE_IDS = new Set(['x', 'twitter']);
const IG_SOURCE_IDS = new Set(['instagram']);

/**
 * 메인 채널 + X·Instagram 세부 유형을 하나의 ES filter 배열로 합친다.
 *
 * 브랜드 멘션(`buildBrandMentionFilters`)과 키워드 모니터링(`buildKeywordMonitoringFilters`)이
 * 공유한다 — 같은 칩 UI를 쓰므로 ES 절도 한 곳에서만 만든다.
 * 세부 유형이 전부 ON(또는 미지정)이면 기존과 동일하게 `source` terms만 남는다.
 *
 * @param {Object} input
 * @param {string[]} [input.platformIds] 메인 채널
 * @param {string[]} [input.xPlatforms] x · x-quote · x-repost
 * @param {string[]} [input.instagramPlatforms] instagram-post · instagram-story
 * @param {Array} [input.filters] 앞에 붙일 기존 filter 배열
 * @returns {Array} filter 디스크립터 배열
 */
export function mergeChannelKindPlatformFilters({
  platformIds = [],
  xPlatforms = [],
  instagramPlatforms = [],
  filters = [],
}: {
  platformIds?: unknown;
  xPlatforms?: unknown;
  instagramPlatforms?: unknown;
  filters?: EsFilterDescriptor[];
} = {}): EsFilterDescriptor[] {
  const base = Array.isArray(filters) ? [...filters] : [];
  const main = Array.isArray(platformIds)
    ? platformIds.map((id: unknown) => String(id).toLowerCase()).filter(Boolean)
    : [];
  const isPartialX = isPartialBrandXKindSelection(xPlatforms);
  const isPartialIg = isPartialBrandInstagramKindSelection(instagramPlatforms);
  const xKindBool = buildBrandXOriginKindEsBoolShould(xPlatforms);
  const igKindBool = buildBrandInstagramKindEsBoolShould(instagramPlatforms);

  if (main.length === 0) {
    if (!isPartialX && !isPartialIg) {
      return base;
    }
    if (isPartialX) {
      base.push(...buildBrandXOriginKindEsMustNotFilters(xPlatforms));
    }
    if (isPartialIg) {
      base.push(...buildBrandInstagramKindEsMustNotFilters(instagramPlatforms));
    }
    return base;
  }

  const nonSpecialMain = main.filter(
    (id) => !X_SOURCE_IDS.has(id) && !IG_SOURCE_IDS.has(id),
  );
  const hasXInMain = main.some((id) => X_SOURCE_IDS.has(id));
  const hasIgInMain = main.some((id) => IG_SOURCE_IDS.has(id));
  const needsBoolShould = (hasXInMain && isPartialX && xKindBool)
    || (hasIgInMain && isPartialIg && igKindBool);

  if (needsBoolShould) {
    const should: EsBoolClause[] = [];
    if (nonSpecialMain.length > 0) {
      should.push({ terms: { source: nonSpecialMain } });
    }
    if (hasXInMain && isPartialX && xKindBool) {
      should.push(xKindBool);
    } else if (hasXInMain && !isPartialX) {
      should.push({ terms: { source: ['x', 'twitter'] } });
    }
    if (hasIgInMain && isPartialIg && igKindBool) {
      should.push(igKindBool);
    } else if (hasIgInMain && !isPartialIg) {
      should.push({ terms: { source: ['instagram'] } });
    }
    base.push({
      type: 'bool_should',
      query: {
        bool: {
          should,
          minimum_should_match: 1,
        },
      },
    });
    return base;
  }

  base.push({ type: 'terms', field: 'source', value: main });
  return base;
}

/**
 * 브랜드명 비교용 정규화 (대소문자, 앞뒤·연속 공백)
 * @param {unknown} value
 * @returns {string}
 */
export function normalizeBrandNameForMatch(value: unknown): string {
  return String(value ?? '')
    .trim()
    .toLowerCase()
    .replace(/\s+/g, ' ');
}

/**
 * CatchMomentsSetting/brandClusters/canonical 문서에서 similarBrands / similarBrand 배열 추출
 * @param {Record<string, unknown>} cluster
 * @returns {unknown[]}
 */
export function getClusterSimilarBrands(cluster: any): unknown[] {
  if (Array.isArray(cluster?.similarBrands)) return cluster.similarBrands;
  if (Array.isArray(cluster?.similarBrand)) return cluster.similarBrand;
  return [];
}

/**
 * `post.canonicalBrands` 매칭용 허용 브랜드명(정규화) 집합을 만든다.
 * - 클러스터(canonical): 등록 키워드가 main/similar에 걸리고 `recognized === true`인 문서의 mainBrand
 * - 등록 브랜드명 전체: 클러스터에 없어도 ES `canonicalBrands`에 그대로 들어올 수 있으므로 합집합에 포함
 * @param {string[]} registeredBrandNames - brandSetting.brandKeyword 등
 * @param {Array<Record<string, unknown>>} brandClusters - getBrandClusters() 결과 (CatchMomentsSetting/brandClusters/canonical)
 * @returns {Set<string>}
 */
export function buildCanonicalMainBrandNormSetForWorkspace(registeredBrandNames: unknown, brandClusters: unknown): Set<string> {
  const regSet = new Set<string>();
  for (const name of Array.isArray(registeredBrandNames) ? registeredBrandNames : []) {
    const n = normalizeBrandNameForMatch(name);
    if (n) regSet.add(n);
  }
  if (regSet.size === 0) return new Set<string>();

  const mainNormSetFromClusters = new Set<string>();
  for (const cluster of Array.isArray(brandClusters) ? brandClusters : []) {
    if (cluster?.recognized !== true) continue;

    const mainN = normalizeBrandNameForMatch(cluster?.mainBrand);
    let touches = Boolean(mainN && regSet.has(mainN));
    if (!touches) {
      for (const s of getClusterSimilarBrands(cluster)) {
        if (regSet.has(normalizeBrandNameForMatch(s))) {
          touches = true;
          break;
        }
      }
    }
    if (touches && mainN) mainNormSetFromClusters.add(mainN);
  }

  return new Set([...mainNormSetFromClusters, ...regSet]);
}


/**
 * `post.canonicalBrands`에 허용 집합(클러스터 mainBrand ∪ 등록 브랜드명) 중 하나라도 있으면 true
 * @param {Record<string, unknown>} post
 * @param {Set<string>} allowedMainBrandNormSet - buildCanonicalMainBrandNormSetForWorkspace 결과
 * @returns {boolean}
 */
export function postMatchesCanonicalBrandMainSet(post: any, allowedMainBrandNormSet: Set<string> | null | undefined): boolean {
  if (!allowedMainBrandNormSet || allowedMainBrandNormSet.size === 0) return false;
  const canonicalBrands = Array.isArray(post?.canonicalBrands) ? post.canonicalBrands : [];
  for (const b of canonicalBrands) {
    const nb = normalizeBrandNameForMatch(b);
    if (nb && allowedMainBrandNormSet.has(nb)) return true;
  }
  return false;
}
